import React, {Component} from 'react'
import {createFragmentContainer, graphql} from 'react-relay'
import {map, prepend} from 'ramda'
import Select from 'react-select'
import classNames from 'classnames'

import FormGroup from 'components/ui/FormGroup'
import type {Option} from 'components/proposals/types'
import {liftEdges} from 'utils/GraphQL'

const availablePolicyEdges = liftEdges('availablePolicies')

const makeOption = ({currentlyUpdating, node: {databaseId, title}}): Option => (
  {
    label: title,
    value: databaseId,
    type: 'UpdatePolicy',
    isDisabled: currentlyUpdating,
  }
)

type Props = {
  className: string,
  disabled: boolean,
  onChange: (command: string) => void,
  proposal: {
    databaseId: string,
  },
}

class PolicySelect extends Component<Props> {
  static defaultProps = {
    className: '',
  }

  selectedPolicyNode = (databaseId: string) => (
    availablePolicyEdges(this.props.proposal)
      .find((edge) => edge.node.databaseId === databaseId)?.node
  )

  onChange = (option: Option) => {
    const {value: databaseId, type} = option
    const command = {databaseId, type}

    const domainDatabaseId = this.selectedPolicyNode(databaseId)?.domain?.databaseId

    if (domainDatabaseId)
      command.domainDatabaseId = domainDatabaseId

    const jsonCommand = JSON.stringify(command)
    this.props.onChange(jsonCommand, `${type}Change`)
  }

  get proposalDatabaseId() {
    return this.props.proposal.databaseId
  }

  get options(): Option[] {
    return prepend(
      {
        // eslint-disable-next-line no-irregular-whitespace
        label: `— ${I18n.t('proposals.controls.new_policy')} —`,
        value: '',
        type: 'CreatePolicy',
      },
      map(makeOption, availablePolicyEdges(this.props.proposal)),
    )
  }

  get className(): string {
    return classNames(this.props.className, 'refinery-select')
  }

  render() {
    return (
      <FormGroup
        label={I18n.t('proposals.controls.add_edit_policy')}
        small
      >
        <Select
          isDisabled={this.props.disabled}
          className={this.className}
          classNamePrefix="Select"
          name={`PartSelector-${this.proposalDatabaseId}-policy-select`}
          noOptionsMessage={() => I18n.t('shared.no_results_found')}
          value={null}
          onChange={this.onChange}
          options={this.options}
          placeholder={I18n.t('proposals.rewrite.select_input_choose_policy')}
        />
      </FormGroup>
    )
  }
}

export default createFragmentContainer(PolicySelect, {
  proposal: graphql`
    fragment PolicySelect_proposal on Proposal {
      databaseId

      availablePolicies {
        edges {
          currentlyUpdating

          node {
            databaseId
            title

            domain {
              databaseId
            }
          }
        }
      }
    }
  `,
})
